import React, { useEffect } from 'react';

import type { SeverityLevel } from '@sentry/react';
import { serializeError } from 'serialize-error';
import { type FormattedTransaction, type Hash, type TransactionReceipt } from 'viem';
import { gql } from '@soundxyz/gql-string';
import { exhaustiveGuard } from '@soundxyz/utils';
import { getChainName } from '@soundxyz/utils/constants/constants';
import {
  ERROR_TYPE,
  MINTING_ERROR_ACTIONS,
  PILLARS,
  TRANSACTION_ERROR_ACTIONS,
  UPLOAD_RELEASE_ERROR_ACTIONS,
} from '@soundxyz/utils/constants/logging';
import { ContractMethod } from '@soundxyz/utils/constants/transaction';
import { useStableCallback } from '@components-shared/src/hooks/useStableCallback';
import { updateReferralWithdrawable } from '@components/Dashboard/Earnings/Referrals/useUpdateReferralWithdrawableBalance';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import { InsufficientFundsModal } from '@components/modals/InsufficientFundsModal';
import { ProcessingModal } from '@components/modals/ProcessingModal';
import { useAuth } from '@context/auth/base';
import { useConnectedAuth } from '@context/auth/connected';
import { ModalContext, ModalType } from '@context/modal';
import type { OpenToastProps } from '@context/toast';
import { ToastContext } from '@context/toast';
import { fetchGQL, useMutation } from '@graphql/reactQuery';
import {
  ClientConfirmedDraftUpsertDocument,
  ClientDraftSplitUpsertDocument,
  ClientNftUpsertDocument,
  GetDraftDocument,
  invalidateOperations,
} from '@graphql/reactQuery';
import { useLogError } from '@hooks/useLogErrorNew';
import { PersistenceStorage } from '@store/persistence';
import { refetchSDK } from '@store/sdk/reactQuery';
import { HELP_PATHS, txProgressMessages } from '@utils/constants';
import { VerifyTransactionError } from '@utils/error';
import { expectChainType } from '@utils/expectSubsetChainName';
import { getTransferEventsInfoFromTxReceipt } from '@utils/transaction';
import {
  isInsufficientFundsError,
  isInternalRpcError,
  isTransactionNotFoundError,
  isUnknownRpcError,
} from '@utils/viemErrors';
import { createContainer } from '../../utils/unstated';
import { getTransactionResponse, TransactionStatusStore } from './TransactionStatusStore';
import { shouldDowngradeError } from './helpers';
import type { ExtendedContractMethod } from './schema';
import { IndividualTransactionStatus, TransactionStatusStoreDataSchema } from './schema';
import { useTransactionStatusUpdate } from './useTransactionStatusUpdate';

type PurchasedNftInfo = {
  nftId: string;
};

/*#__PURE__*/ gql(/* GraphQL */ `
  mutation clientNftUpsert($txHash: String!, $chain: ChainType!) {
    clientNftUpsert(txHash: $txHash, chain: $chain) {
      id
      tokenId
      # this ensures that user ownership on BE is updated
      release {
        id
      }
      owner {
        id
      }
    }
  }

  mutation clientConfirmedDraftUpsert($hash: String!, $chain: ChainType!) {
    clientConfirmedDraftUpsert(hash: $hash, chain: $chain) {
      id
    }
  }

  mutation clientDraftSplitUpsert($txHash: String!) {
    clientDraftSplitUpsert(txHash: $txHash) {
      id
    }
  }

  mutation updateReferralWithdrawableBalance {
    updateReferralWithdrawableBalance {
      id
      lastReferralWithdrawableBalance
    }
  }
`);

const TransactionStatusPersistence = PersistenceStorage({
  key: `@soundxyz/transactionStatusStore-0.1`,
  schema: TransactionStatusStoreDataSchema,
  eager: true,
});

const transactionStatusStore = new TransactionStatusStore({
  // TODO: add tests to the library
  onChange: data => {
    TransactionStatusPersistence.set(data);
  },
});

export function resetTransactionStatusStore() {
  transactionStatusStore.reset();
}

export function useTransactionHandler() {
  const { onTransactionStatusUpdate } = useTransactionStatusUpdate();
  const [pending, setPending] = React.useState(false);
  const [openProcessingModal, setOpenProcessingModal] = React.useState(true);
  const { openModal } = ModalContext.useContainer();
  const { mutateAsync: clientDraftSplitUpsert, error: draftSplitUpsertError } = useMutation(
    ClientDraftSplitUpsertDocument,
    {},
  );
  const { mutateAsync: clientNftUpsert, error: nftUpsertError } = useMutation(
    ClientNftUpsertDocument,
    {
      retry: 5,
    },
  );
  const logError = useLogError();
  const { authUser } = useAuth();
  const { provider, walletAddress, loginRequireConnection } = useConnectedAuth();
  const { closeToast, openToast } = ToastContext.useContainer();

  const [purchasedNftInfo, setPurchasedNftInfo] = React.useState<PurchasedNftInfo | undefined>();

  const { closeModalByType } = ModalContext.useContainer();

  // upsert atleast one nft so user can go to comment flow
  const _upsertNftFromPurchase = useStableCallback(
    async ({ receipt, chainId }: { receipt: TransactionReceipt; chainId: number }) => {
      const { transactionHash } = receipt;

      try {
        const nft = await clientNftUpsert({
          txHash: transactionHash,
          chain: expectChainType(getChainName(chainId)),
        });

        setPurchasedNftInfo({
          nftId: nft.data?.clientNftUpsert?.id || '',
        });
      } catch (err) {
        logError({
          action: MINTING_ERROR_ACTIONS.NFT_UPSERT_ERROR,
          error: err,
          errorType: ERROR_TYPE.NFT_UPSERT_MUTATION_ERROR,
          message: 'Error upserting NFT',
          level: 'fatal',
          isArtistOnlyPage: false,
          pillar: PILLARS.MINTING,
        });
      }
    },
  );

  const _confirmDraftDeploySplitClientSide = useStableCallback(
    async ({ receipt }: { receipt: TransactionReceipt }) => {
      try {
        const { status, transactionHash } = receipt;
        if (status === 'reverted') {
          closeToast();
          const message = 'Transaction failed!';
          logError({
            action: UPLOAD_RELEASE_ERROR_ACTIONS.RELEASE_DRAFT_DEPLOY_ERROR,
            error: new Error('Transaction failed!'),
            errorType: ERROR_TYPE.DEPLOY_SPLITS_TRANSACTION_FAILED,
            toast: message,
            message,
            pillar: PILLARS.UPLOAD_RELEASE,
            isArtistOnlyPage: true,
            level: 'fatal',
            indexedTags: {
              txHash: transactionHash,
            },
            unindexedExtra: {
              receipt,
            },
          });
          return;
        } else if (status === 'success') {
          await clientDraftSplitUpsert({ txHash: transactionHash });
        } else {
          exhaustiveGuard(status);
        }
      } catch (err) {
        const message = 'Transaction failed!';
        logError({
          action: UPLOAD_RELEASE_ERROR_ACTIONS.RELEASE_DRAFT_DEPLOY_ERROR,
          error: err,
          errorType: ERROR_TYPE.DEPLOY_SPLITS_TRANSACTION_FAILED,
          message,
          pillar: PILLARS.UPLOAD_RELEASE,
          isArtistOnlyPage: true,
          level: 'fatal',
          unindexedExtra: {
            receipt,
          },
        });
      }
    },
  );

  const _confirmCreateDraftNewEditionClientSide = useStableCallback(
    async ({ receipt, chainId }: { receipt: TransactionReceipt; chainId: number }) => {
      try {
        const { status, transactionHash } = receipt;
        if (status === 'reverted') {
          closeToast();
          const message = 'Transaction failed!';
          logError({
            action: UPLOAD_RELEASE_ERROR_ACTIONS.RELEASE_DRAFT_DEPLOY_ERROR,
            error: new Error('Transaction failed!'),
            errorType: ERROR_TYPE.CREATE_DRAFT_EDITION_TRANSACTION_FAILED,
            toast: message,
            message,
            pillar: PILLARS.UPLOAD_RELEASE,
            isArtistOnlyPage: true,
            level: 'fatal',
            indexedTags: {
              txHash: transactionHash,
              chainId,
            },
            unindexedExtra: {
              receipt,
            },
          });
          return;
        }

        const { errors } = await fetchGQL(ClientConfirmedDraftUpsertDocument, {
          variables: { hash: transactionHash, chain: expectChainType(getChainName(chainId)) },
        });
        if (errors?.length) throw new Error(errors[0]?.message);
      } catch (err) {
        const message = 'Transaction failed!';
        logError({
          action: UPLOAD_RELEASE_ERROR_ACTIONS.RELEASE_DRAFT_DEPLOY_ERROR,
          error: err,
          errorType: ERROR_TYPE.NFT_TXN_UPSERT_MUTATION_FAILED,
          toast: message,
          message,
          pillar: PILLARS.UPLOAD_RELEASE,
          isArtistOnlyPage: true,
          level: 'fatal',
          indexedTags: {
            chainId,
          },
          unindexedExtra: {
            receipt,
          },
        });
      }
    },
  );

  // While we confirm the transaction serverside, we will also attempt to confirm it client side
  // For buyEdition, we want to parse the purchased Nft so the user to leave a comment at the earliest tx confirmation
  // We also handle the special case of replacement transactions which occurs when a user "speeds up"
  // a transaction by submitting a new one, this invalidates the prior registered txHash
  const _handleClientSideTxConfirmation = useStableCallback(
    async ({
      contractMethod,
      tx,
      chainId,
    }: {
      contractMethod: ExtendedContractMethod;
      tx: FormattedTransaction;
      chainId: number;
    }) => {
      try {
        const receipt = await provider(chainId).waitForTransactionReceipt({ hash: tx.hash });

        if (contractMethod === ContractMethod.DRAFT_SPLIT_MAIN__CREATE_SPLIT) {
          await _confirmDraftDeploySplitClientSide({ receipt });
        } else if (
          contractMethod === ContractMethod.ARTIST__BUY_EDITION ||
          contractMethod === ContractMethod.SAM__BUY_EDITION ||
          contractMethod === ContractMethod.COLLECTOR__TRANSFER_EDITION
        ) {
          await _upsertNftFromPurchase({ receipt, chainId });
        } else if (contractMethod === ContractMethod.DRAFT_SOUND_CREATOR__CREATE_SOUND_AND_MINTS) {
          await _confirmCreateDraftNewEditionClientSide({ receipt, chainId });

          invalidateOperations({
            operations: [GetDraftDocument],
          });
        } else if (contractMethod === ContractMethod.AFFILIATE__WITHDRAW && authUser) {
          updateReferralWithdrawable();
        }
      } catch (error) {
        const message = `Unhandled transaction error`;
        logError({
          action: TRANSACTION_ERROR_ACTIONS.TRANSACTION_ERROR,
          error,
          errorType: ERROR_TYPE.CANCELLED_TRANSACTION,
          message,
          level: 'warning',
          pillar: PILLARS.TRANSACTION,
          isArtistOnlyPage: false,
          indexedTags: { chainId, contractMethod, transactionHash: tx.hash },
        });
      } finally {
        refetchSDK();
      }
    },
  );

  const _handleTransactionError = useStableCallback(
    async (args: { error: unknown; txHash?: string; chainId?: number }) => {
      const { error } = args;
      const txHash = error instanceof VerifyTransactionError ? error.txHash : args.txHash;

      const chainId = error instanceof VerifyTransactionError ? error.chainId : args.chainId;

      let toast: OpenToastProps | undefined;
      let toastDuration: number | undefined;
      let level: SeverityLevel | undefined;

      logError({
        action: TRANSACTION_ERROR_ACTIONS.TRANSACTION_ERROR,
        error,
        errorType: ERROR_TYPE.UNKNOWN,
        message: `Unknown error for transaction ${txHash}`,
        toast,
        toastDuration,
        // TODO: Remove `file` when we add source maps https://linear.app/sound/issue/ENG-1280/sentry-sourcemaps
        indexedTags: {
          file: 'transaction.tsx',
          txHash,
          chainId,
        },
        level: level ?? 'warning',
        closeProcessingModal: true,
        pillar: PILLARS.TRANSACTION,
        isArtistOnlyPage: false,
      });
    },
  );

  const [rehydrateAttempted, setRehydrateAttempted] = React.useState(false);

  useEffect(() => {
    transactionStatusStore.setOnTransactionProcessing(transaction => {
      setPending(true);

      switch (transaction.contractMethod) {
        // These transactions have custom modals
        case ContractMethod.SOUND_CREATOR__CREATE_SOUND_AND_MINTS:
        case ContractMethod.DRAFT_SOUND_CREATOR__CREATE_SOUND_AND_MINTS:
        case ContractMethod.DRAFT_SPLIT_MAIN__CREATE_SPLIT:
          break;
        default:
          openProcessingModal &&
            openModal(ModalType.PROCESSING, {
              body: (
                <ProcessingModal
                  heading={txProgressMessages[transaction.contractMethod]}
                  transactionInfo={{
                    txHash: transaction.hash,
                    chainId: transaction.chainId,
                  }}
                />
              ),
            });
      }
    });
    transactionStatusStore.setOnTransactionComplete(async ({ lineItem, txResponse }) => {
      if (txResponse && lineItem.status === IndividualTransactionStatus.SUCCESS) {
        await _handleClientSideTxConfirmation({
          contractMethod: lineItem.transaction.contractMethod,
          tx: txResponse,
          chainId: lineItem.transaction.chainId,
        });
      }

      const receipt = await (txResponse?.chainId != null
        ? provider(txResponse.chainId).waitForTransactionReceipt({
            hash: txResponse.hash,
          })
        : null);

      const transfersInTx = receipt && getTransferEventsInfoFromTxReceipt(receipt);
      const quantity = transfersInTx?.length ?? 1;

      // ensure this is called for both failure and success
      await onTransactionStatusUpdate({ lineItem, quantity });

      closeModalByType(ModalType.PROCESSING);

      setPending(false);
    });

    if (rehydrateAttempted) return;

    setRehydrateAttempted(true);

    TransactionStatusPersistence.initialValue.then(data => {
      if (!data) return;

      transactionStatusStore.hydrate(data);

      transactionStatusStore
        .verifyUnknownTransactions({
          now: new Date(),
          provider,
        })
        .catch(error => _handleTransactionError({ error }));
    });
  }, [
    _handleClientSideTxConfirmation,
    _handleTransactionError,
    closeModalByType,
    onTransactionStatusUpdate,
    openModal,
    openProcessingModal,
    provider,
    rehydrateAttempted,
  ]);

  // Initiates the transaction prompt in a user's wallet
  const _safePromptUsertoSubmitTx = useStableCallback(async function _safePromptUsertoSubmitTx({
    transactionFn,
    showOpenWalletToast,
    chainId,
  }: {
    transactionFn: () => Promise<Hash>;
    showOpenWalletToast: boolean;
    chainId: number;
  }): Promise<Hash | null> {
    if (showOpenWalletToast) {
      openToast({
        text: 'Please confirm the transaction in your wallet.',
      });
    }

    try {
      const transactionHash = await transactionFn();
      return transactionHash;
    } catch (err) {
      const error = serializeError(err);

      if (error.message?.includes('User denied transaction signature')) {
        openToast({
          text: 'Transaction cancelled.',
        });
      } else if (isInsufficientFundsError(err)) {
        closeToast();

        logError({
          action: TRANSACTION_ERROR_ACTIONS.TRANSACTION_ERROR,
          error: err,
          errorType: ERROR_TYPE.UNKNOWN,
          level: 'warning',
          message: `Insufficient funds error when prompting user to submit transaction`,
          pillar: PILLARS.TRANSACTION,
          isArtistOnlyPage: false,
          indexedTags: {
            file: 'transaction.tsx',
            context: 'promptUserToSubmitTx',
            chainId,
          },
        });

        openModal(ModalType.INSUFFICIENT_FUNDS, {
          body: (
            <InsufficientFundsModal
              description="Insufficient funds to complete transaction. Please add funds to your wallet and try again."
              chainId={chainId}
              primaryButton={null}
              secondaryButton={null}
            />
          ),
        });

        return null;
      } else if (isUnknownRpcError(err) || isInternalRpcError(err)) {
        closeToast();

        const toastMsg =
          'Wallet returned an error. Please try again after logging out and reconnecting';

        logError({
          action: TRANSACTION_ERROR_ACTIONS.TRANSACTION_ERROR,
          error: err,
          errorType: ERROR_TYPE.UNKNOWN,
          level: 'warning',
          message: toastMsg,
          toast: toastMsg,
          pillar: PILLARS.TRANSACTION,
          isArtistOnlyPage: false,
          indexedTags: {
            file: 'transaction.tsx',
            context: 'promptUserToSubmitTx',
            chainId,
          },
        });

        return null;
      } else if (isTransactionNotFoundError(err)) {
        closeToast();

        const toastMsg =
          'Transaction pending. Please check your wallet to speed up the pending transaction.';

        logError({
          action: TRANSACTION_ERROR_ACTIONS.TRANSACTION_ERROR,
          error: err,
          errorType: ERROR_TYPE.UNKNOWN,
          level: 'warning',
          message: toastMsg,
          toast: toastMsg,
          pillar: PILLARS.TRANSACTION,
          isArtistOnlyPage: false,
          indexedTags: {
            file: 'transaction.tsx',
            context: 'promptUserToSubmitTx',
            chainId,
          },
        });

        return null;
      } else {
        closeToast();

        logError({
          action: TRANSACTION_ERROR_ACTIONS.TRANSACTION_ERROR,
          error: err,
          errorType: ERROR_TYPE.UNKNOWN,
          level: shouldDowngradeError(err) ? 'warning' : 'fatal',
          message: 'Error when prompting user to submit transaction',
          pillar: PILLARS.TRANSACTION,
          isArtistOnlyPage: false,
          indexedTags: {
            // TODO: Remove `file` & `context` when we add source maps https://linear.app/sound/issue/ENG-1280/sentry-sourcemaps
            file: 'transaction.tsx',
            context: 'promptUserToSubmitTx',
            chainId,
          },
          toast: {
            variant: 'error',
            text: (
              <>
                Something went wrong.{' '}
                <AccessibleLink
                  absoluteUri={HELP_PATHS.RequestHelp}
                  className="font-semibold text-white underline"
                  type="external"
                >
                  Contact us{' '}
                </AccessibleLink>
                for assistance.
              </>
            ),
          },
        });
      }

      // If something went wrong, we should always close the processing modals
      closeModalByType(ModalType.PROCESSING_UPLOAD_JOURNEY);
      closeModalByType(ModalType.PROCESSING);
      return null;
    }
  });

  // After the transaction is initiated, store the txHash in our db
  const _registerTxHashWithRetries = useStableCallback(async function _registerTxHashWithRetries(
    txHash: string,
    registerTransactionFn: (txHash: string) => Promise<void>,
  ) {
    // register the tx with our backend
    try {
      // try registering 3 times
      let count = 0;
      const maxTries = 3;
      while (true) {
        try {
          await registerTransactionFn(txHash);
          break;
        } catch (err) {
          // handle exception
          if (++count === maxTries) throw err;
        }
      }
    } catch (err) {
      closeToast();
      logError({
        action: TRANSACTION_ERROR_ACTIONS.TRANSACTION_ERROR,
        error: err,
        errorType: ERROR_TYPE.TRANSACTION_REGISTER_ERROR,
        message: `Error registering transaction`,
        toast: 'Something went wrong while sending the transaction.',
        indexedTags: {
          file: 'transaction.tsx',
          txHash,
        },
        level: 'fatal',
        pillar: PILLARS.TRANSACTION,
        isArtistOnlyPage: false,
      });
      return false;
    }
    return true;
  });

  const safeHandleTransaction = useStableCallback(async function safeHandleTransaction({
    contractMethod,
    transactionFn,
    registerTransactionFn,
    releaseId,
    showOpenWalletToast = true,
    chainId,
    openProcessingModal: propOpenProcessingModal,
  }: {
    contractMethod: ExtendedContractMethod;
    transactionFn: () => Promise<Hash>;
    registerTransactionFn?: (txHash: string) => Promise<void>;
    releaseId?: string; // TODO: remove this leaky abstraction: https://linear.app/sound/issue/INFRA-956/go-over-the-todos-remove-leaky-abstraction-add-try-catch-add-tests
    /** Whether or not to show the "Please confirm the transaction in your wallet" toast. */
    showOpenWalletToast?: boolean;
    chainId: number;
    openProcessingModal: boolean;
  }): Promise<
    { success: true; transactionHash: Hash } | { success: false; transactionHash: Hash | null }
  > {
    // ensure user is connected
    if (!walletAddress) {
      const auth = await loginRequireConnection();
      if (auth?.stop) {
        setPending(false);
        return {
          success: false,
          transactionHash: null,
        };
      }
    }

    // mark processing
    setOpenProcessingModal(propOpenProcessingModal);
    setPending(true);

    // get user to make the transaction, this fn handles logging errors and related toasts
    const transactionHash = await _safePromptUsertoSubmitTx({
      transactionFn,
      showOpenWalletToast,
      chainId,
    });

    if (!transactionHash) {
      setPending(false);
      return {
        success: false,
        transactionHash,
      };
    }

    const chainProvider = provider(chainId);
    const tx = await getTransactionResponse({
      chainId,
      hash: transactionHash,
      provider: chainProvider,
    }).catch(error => {
      logError({
        error,
        message: 'Error getting transaction response',
        action: TRANSACTION_ERROR_ACTIONS.TRANSACTION_ERROR,
        errorType: ERROR_TYPE.MISSING_TRANSACTION_HASH,
        isArtistOnlyPage: false,
        pillar: 'TRANSACTION',
        level: 'warning',
        toast: 'Transaction status not found. Please check your wallet for the transaction',
      });
      return null;
    });

    if (!tx) {
      setPending(false);
      return {
        success: false,
        transactionHash,
      };
    }
    // TODO: expose the store's api to to the hook so we can see pending transactions

    try {
      registerTransactionFn &&
        (await _registerTxHashWithRetries(transactionHash, registerTransactionFn));

      await transactionStatusStore.onNewTransaction({
        txResponse: tx,
        contractMethod,
        releaseId,
        provider: chainProvider,
        chainId,
      });

      return {
        success: true,
        transactionHash: tx.hash,
      };
    } catch (error) {
      _handleTransactionError({ error, txHash: tx.hash, chainId });

      return {
        success: false,
        transactionHash: tx.hash,
      };
    } finally {
      closeModalByType(ModalType.PROCESSING);
      setPending(false);
    }
  });

  React.useEffect(() => {
    if (nftUpsertError) {
      logError({
        action: MINTING_ERROR_ACTIONS.NFT_UPSERT_ERROR,
        error: nftUpsertError,
        errorType: ERROR_TYPE.NFT_UPSERT_MUTATION_ERROR,
        message: 'Error upserting NFT',
        level: 'fatal',
        isArtistOnlyPage: false,
        pillar: PILLARS.MINTING,
      });
    }
  }, [nftUpsertError, logError]);

  React.useEffect(() => {
    if (draftSplitUpsertError) {
      logError({
        action: UPLOAD_RELEASE_ERROR_ACTIONS.RELEASE_DRAFT_DEPLOY_ERROR,
        error: draftSplitUpsertError,
        errorType: ERROR_TYPE.DEPLOY_SPLITS_TRANSACTION_FAILED,
        message: 'Error deploying splits',
        level: 'fatal',
        pillar: PILLARS.UPLOAD_RELEASE,
        isArtistOnlyPage: true,
      });
    }
  }, [logError, draftSplitUpsertError]);

  return {
    safeHandleTransaction,
    pending,
    purchasedNftInfo,
    resetTransactionStatusStore,
  };
}

export const Transaction = createContainer(useTransactionHandler);

export const useTransaction = () => Transaction.useContainer();
